// Global
import { tv } from 'tailwind-variants';

export type SkeletonCardProps = {
  variant?: 'expert' | 'article' | 'articleSearch' | 'default';
  orientation?: 'vertical' | 'horizontal';
};

const SkeletonCard = ({ variant = 'default', orientation = 'vertical' }: SkeletonCardProps) => {
  const { card, imageSection, contentSection, line, lineLong, lineMedium, lineShort, ctaLine } =
    TAILWIND_VARIANTS({ variant, orientation });

  return (
    <div className={card()}>
      {/* Image Skeleton */}
      <div className={imageSection()} />

      {/* Content Skeleton */}
      <div className={contentSection()}>
        <div className={lineLong()} />
        <div className={lineMedium()} />
        <div className={line()} />
        <div className={lineShort()} />
        <div className={ctaLine()} />
      </div>
    </div>
  );
};

export default SkeletonCard;

const TAILWIND_VARIANTS = tv({
  slots: {
    card: [
      'flex',
      'flex-col',
      'w-full',
      'h-full',
      'overflow-hidden',
      'rounded-general-border-radius-card',
      'shadow-color-black-alpha-10',
      'shadow-[0_0_8px_0]',
      'bg-white',
    ],
    imageSection: [
      'relative',
      'w-full',
      'bg-gray-300',
      'animate-pulse',
      'rounded-t-general-border-radius-card',
    ],
    contentSection: [
      'flex',
      'flex-col',
      'flex-1',
      'gap-spacing-spacing-16',
      'p-spacing-spacing-24',
      'rounded-b-general-border-radius-card',
      'min-h-[300px]', // Match typical card content height
    ],
    line: ['h-4', 'bg-gray-400', 'rounded', 'animate-pulse'],
    lineLong: ['h-4', 'bg-gray-400', 'rounded', 'w-3/4', 'animate-pulse'],
    lineMedium: ['h-4', 'bg-gray-400', 'rounded', 'w-2/3', 'animate-pulse'],
    lineShort: ['h-4', 'bg-gray-400', 'rounded', 'w-1/2', 'animate-pulse'],
    ctaLine: ['h-8', 'bg-gray-400', 'rounded', 'w-1/3', 'animate-pulse', 'mt-auto'],
  },
  variants: {
    variant: {
      expert: {
        imageSection: ['aspect-[4/3]'],
        contentSection: ['bg-component-featureCard-surface'],
      },
      article: {
        imageSection: ['aspect-video'],
        contentSection: ['bg-component-featureCard-surface'],
      },
      articleSearch: {
        imageSection: ['aspect-video'],
        contentSection: ['bg-component-card-item-surface'],
      },
      default: {
        imageSection: ['aspect-video'],
        contentSection: ['bg-component-featureCard-surface'],
      },
    },
    orientation: {
      vertical: {
        card: ['flex-col'],
        imageSection: ['w-full', 'rounded-t-general-border-radius-card'],
        contentSection: ['rounded-b-general-border-radius-card'],
      },
      horizontal: {
        card: ['flex-col', 'md:flex-row', 'lg:flex-col'],
        imageSection: [
          'w-full',
          'md:w-1/2',
          'lg:w-full',
          'rounded-t-general-border-radius-card',
          'md:rounded-l-general-border-radius-card',
          'md:rounded-tr-none',
          'lg:rounded-l-none',
          'lg:rounded-t-general-border-radius-card',
        ],
        contentSection: [
          'w-full',
          'md:w-1/2',
          'lg:w-full',
          'rounded-b-general-border-radius-card',
          'md:rounded-r-general-border-radius-card',
          'md:rounded-bl-none',
          'lg:rounded-r-none',
          'lg:rounded-b-general-border-radius-card',
        ],
      },
    },
  },
});
